import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogOut, Plus, Trash2 } from "lucide-react";
import { adminApi } from "../../lib/api";
import { BUSINESS } from "../../data/site";

const INQUIRY_STATUSES = ["new", "contacted", "closed"];

const CONTENT_SCHEMAS = {
  reviews: [
    { key: "name", label: "Name" },
    { key: "location", label: "Location" },
    { key: "text", label: "Review Text", textarea: true },
    { key: "order", label: "Order", number: true },
  ],
  faqs: [
    { key: "page", label: "Page Key (rolloff / contractors / frontload-residential / frontload-commercial / sizeguide)" },
    { key: "question", label: "Question" },
    { key: "answer", label: "Answer", textarea: true },
    { key: "order", label: "Order", number: true },
  ],
  gallery: [
    { key: "type", label: "Type (image or placeholder)" },
    { key: "src", label: "Image Path (for image type)" },
    { key: "alt", label: "Alt Text" },
    { key: "caption", label: "Caption" },
    { key: "label", label: "Placeholder Label (for placeholder type)" },
    { key: "order", label: "Order", number: true },
  ],
  resources: [
    { key: "slug", label: "Slug (url)" },
    { key: "title", label: "Title" },
    { key: "excerpt", label: "Excerpt", textarea: true },
    { key: "date", label: "Date (YYYY-MM-DD)" },
    { key: "image", label: "Image Path" },
    { key: "body", label: 'Body JSON: [{"heading": "...", "text": "..."}]', textarea: true, json: true },
  ],
};

function InquiriesTab() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const load = useCallback(() => {
    adminApi().get("/admin/inquiries", { params: filter ? { type: filter } : {} }).then((r) => setItems(r.data)).catch(() => {});
  }, [filter]);
  useEffect(load, [load]);

  const setStatus = async (id, status) => {
    await adminApi().patch(`/admin/inquiries/${id}`, { status });
    load();
  };

  return (
    <div data-testid="inquiries-tab">
      <div className="flex flex-wrap gap-2 mb-5">
        {["", "roll-off", "contractor", "front-load"].map((t) => (
          <button
            key={t || "all"}
            onClick={() => setFilter(t)}
            className={`px-4 py-2 rounded-lg text-sm font-heading font-semibold transition-colors ${filter === t ? "bg-sky-600 text-white" : "bg-white border border-slate-200 text-slate-700 hover:border-sky-400"}`}
            data-testid={`filter-${t || "all"}`}
          >
            {t ? t.replace("-", " ") : "All"}
          </button>
        ))}
      </div>
      {items.length === 0 && <p className="text-slate-500 text-sm">No inquiries yet.</p>}
      <div className="space-y-3">
        {items.map((inq) => (
          <div key={inq.id} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`inquiry-${inq.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-3">
                <span className="px-2.5 py-1 rounded-full bg-sky-50 text-sky-800 text-xs font-heading font-semibold uppercase tracking-wide">{inq.type}</span>
                <span className="font-heading font-bold text-slate-900">{inq.name || inq.company}</span>
                <span className="text-xs text-slate-400">{new Date(inq.created_at).toLocaleString()}</span>
              </div>
              <select
                value={inq.status}
                onChange={(e) => setStatus(inq.id, e.target.value)}
                className="text-sm border border-slate-200 rounded-lg px-3 py-1.5"
                data-testid={`inquiry-status-${inq.id}`}
                aria-label="Inquiry status"
              >
                {INQUIRY_STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <dl className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1.5 text-sm">
              {Object.entries(inq)
                .filter(([k, v]) => v && !["id", "status", "created_at", "type"].includes(k))
                .map(([k, v]) => (
                  <div key={k} className="flex gap-2">
                    <dt className="text-slate-500 capitalize shrink-0">{k.replace(/_/g, " ")}:</dt>
                    <dd className="text-slate-800 break-words">{String(v)}</dd>
                  </div>
                ))}
            </dl>
          </div>
        ))}
      </div>
    </div>
  );
}

function ContentTab({ kind }) {
  const fields = CONTENT_SCHEMAS[kind];
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => {
    adminApi().get(`/admin/content/${kind}`).then((r) => setItems(r.data)).catch(() => {});
  }, [kind]);
  useEffect(load, [load]);

  const save = async () => {
    const payload = { ...editing };
    for (const f of fields) {
      if (f.number && payload[f.key] !== undefined) payload[f.key] = Number(payload[f.key]) || 0;
      if (f.json && typeof payload[f.key] === "string") {
        try {
          payload[f.key] = JSON.parse(payload[f.key]);
        } catch {
          alert("Body must be valid JSON");
          return;
        }
      }
    }
    if (payload.id) {
      await adminApi().put(`/admin/content/${kind}/${payload.id}`, payload);
    } else {
      await adminApi().post(`/admin/content/${kind}`, payload);
    }
    setEditing(null);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await adminApi().delete(`/admin/content/${kind}/${id}`);
    load();
  };

  return (
    <div data-testid={`content-tab-${kind}`}>
      <button
        onClick={() => setEditing({})}
        className="mb-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-heading font-semibold transition-colors"
        data-testid={`add-${kind}`}
      >
        <Plus className="h-4 w-4" /> Add {kind.slice(0, -1)}
      </button>

      {editing && (
        <div className="mb-6 bg-white rounded-xl border-2 border-sky-200 p-5 space-y-3" data-testid="content-editor">
          {fields.map((f) => (
            <div key={f.key}>
              <label className="block text-xs font-medium text-slate-600 mb-1">{f.label}</label>
              {f.textarea ? (
                <textarea
                  rows={f.json ? 6 : 3}
                  value={f.json && typeof editing[f.key] !== "string" ? JSON.stringify(editing[f.key] || [], null, 2) : editing[f.key] || ""}
                  onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-sky-600 focus:outline-none"
                  data-testid={`editor-${f.key}`}
                />
              ) : (
                <input
                  value={editing[f.key] || ""}
                  onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-sky-600 focus:outline-none"
                  data-testid={`editor-${f.key}`}
                />
              )}
            </div>
          ))}
          <div className="flex gap-3">
            <button onClick={save} className="px-5 py-2.5 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-heading font-semibold" data-testid="editor-save">Save</button>
            <button onClick={() => setEditing(null)} className="px-5 py-2.5 rounded-lg border border-slate-300 text-sm font-heading font-semibold text-slate-700" data-testid="editor-cancel">Cancel</button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {items.map((item) => (
          <div key={item.id} className="bg-white rounded-lg border border-slate-200 px-4 py-3 flex items-center justify-between gap-3" data-testid={`content-item-${item.id}`}>
            <button onClick={() => setEditing(item)} className="text-left flex-1 text-sm font-medium text-slate-800 hover:text-sky-700" data-testid={`edit-${item.id}`}>
              {item.name || item.question || item.title || item.caption || item.label || item.id}
            </button>
            <button onClick={() => remove(item.id)} aria-label="Delete" className="p-2 text-slate-400 hover:text-slate-800" data-testid={`delete-${item.id}`}>
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function AnalyticsTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    adminApi().get("/admin/analytics").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return <Loader2 className="h-6 w-6 animate-spin text-sky-600" />;

  const stats = [
    { label: "Unique Visitors (All Time)", value: data.unique_all, testid: "stat-unique-all" },
    { label: "Unique Visitors (Last 30 Days)", value: data.unique_30d, testid: "stat-unique-30d" },
    { label: "Unique Visitors Today", value: data.unique_today, testid: "stat-unique-today" },
    { label: "Total Pageviews", value: data.total_pageviews, testid: "stat-pageviews" },
  ];

  return (
    <div data-testid="analytics-tab" className="space-y-8">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.testid} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={s.testid}>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{s.label}</p>
            <p className="font-heading font-extrabold text-3xl text-slate-900 mt-1">{s.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <h3 className="font-heading font-bold text-lg text-slate-900 px-5 py-4 border-b border-slate-100">Button Clicks</h3>
        {data.clicks.length === 0 ? (
          <p className="text-sm text-slate-500 px-5 py-4">No button clicks recorded yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left">
                <th className="px-5 py-2.5 font-heading font-semibold text-slate-600">Button</th>
                <th className="px-5 py-2.5 font-heading font-semibold text-slate-600">Clicks</th>
                <th className="px-5 py-2.5 font-heading font-semibold text-slate-600 hidden sm:table-cell">Last Clicked</th>
              </tr>
            </thead>
            <tbody>
              {data.clicks.map((c) => (
                <tr key={c.label} className="border-t border-slate-100" data-testid={`click-row-${c.label}`}>
                  <td className="px-5 py-2.5 text-slate-800">{c.label.replace(/-/g, " ")}</td>
                  <td className="px-5 py-2.5 font-heading font-bold text-sky-700">{c.count}</td>
                  <td className="px-5 py-2.5 text-slate-500 hidden sm:table-cell">{new Date(c.last).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <h3 className="font-heading font-bold text-lg text-slate-900 px-5 py-4 border-b border-slate-100">Top Pages</h3>
        {data.pages.length === 0 ? (
          <p className="text-sm text-slate-500 px-5 py-4">No pageviews recorded yet.</p>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {data.pages.map((p) => (
                <tr key={p.path} className="border-t border-slate-100">
                  <td className="px-5 py-2.5 text-slate-800">{p.path}</td>
                  <td className="px-5 py-2.5 font-heading font-bold text-sky-700 text-right">{p.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const TABS = [
  { key: "inquiries", label: "Inquiries" },
  { key: "analytics", label: "Analytics" },
  { key: "reviews", label: "Reviews" },
  { key: "faqs", label: "FAQs" },
  { key: "gallery", label: "Gallery" },
  { key: "resources", label: "Resources" },
];

export default function AdminDashboard() {
  const [tab, setTab] = useState("inquiries");
  const [checking, setChecking] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    adminApi()
      .get("/auth/me")
      .then(() => setChecking(false))
      .catch(() => navigate("/admin"));
  }, [navigate]);

  if (checking) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-sky-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="bg-ink text-white">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={BUSINESS.logoDark} alt="YWS" className="h-8 w-auto" />
            <span className="font-heading font-semibold">Admin</span>
          </div>
          <button
            onClick={() => { localStorage.removeItem("yws_admin_token"); navigate("/admin"); }}
            className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white"
            data-testid="admin-logout"
          >
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </header>
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex flex-wrap gap-2 mb-6">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 rounded-lg text-sm font-heading font-semibold transition-colors ${tab === t.key ? "bg-ink text-white" : "bg-white border border-slate-200 text-slate-700 hover:border-sky-400"}`}
              data-testid={`tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        {tab === "inquiries" ? <InquiriesTab /> : tab === "analytics" ? <AnalyticsTab /> : <ContentTab kind={tab} />}
      </div>
    </div>
  );
}
